<template>
    <TVJoyDialog>
        <template #dialog-header>{{ $t('projectclient.quote.view.quote-summary.dialog.decline.title') }}</template>
        <template #dialog-subheader>{{ $t('projectclient.quote.view.quote-summary.dialog.decline.subtitle') }}</template>

        <template #dialog-body>
            <VJoyRadioGroup direction="vertical">
                <VJoyRadio v-for="reason in declineReasons" :key="reason.value" v-model="r$.$value.declineReason" :value="reason.value">
                    {{ reason.label }}
                </VJoyRadio>
            </VJoyRadioGroup>
            <VJoyFormError v-if="r$.declineReason?.$error" class="joy-mt-2">
                {{ $t('projectclient.quote.view.quote-summary.dialog.decline.form.errors.reason') }}
            </VJoyFormError>
            <VJoyTextarea
                v-model="r$.$value.freelancerMessage"
                :label="$t('projectclient.quote.view.quote-summary.dialog.decline.message.label', [freelancerName])"
                :placeholder="$t('projectclient.quote.view.quote-summary.dialog.decline.message.placeholder', [freelancerName])"
                :invalid="r$.freelancerMessage?.$error"
            ></VJoyTextarea>
            <VJoyFormError v-if="r$.freelancerMessage?.$error" class="joy-mt-2">
                {{ $t('projectclient.quote.view.quote-summary.dialog.decline.form.errors.message') }}
            </VJoyFormError>
        </template>

        <template #dialog-confirm>
            <VJoyButton variant="ghost" @click="closeDialog">{{ $t('general.cancel') }}</VJoyButton>
            <VJoyButton variant="main" :loading="loading" @click="handleConfirm">
                {{ $t('projectclient.quote.view.quote-summary.dialog.decline.confirm') }}
            </VJoyButton>
        </template>
    </TVJoyDialog>
</template>

<script setup lang="ts">
    import {ref, computed} from 'vue';
    import {required, maxLength} from '@regle/rules';
    import {useTranslation, useLogger} from '#imports';
    import {useRegle} from '@malt/forms';
    import {VJoyButton, VJoyTextarea, VJoyFormError, VJoyRadioGroup, VJoyRadio} from '@maltjoy/core-vue';
    import type {DeclineQuoteRequestReasonEnum} from '@api';
    import {useDialogRoot} from '~/common/composables';
    import {useClientQuoteViewStore} from '../../../stores';

    const props = defineProps<{
        quoteId: string;
        freelancerName: string;
    }>();

    const {t} = useTranslation();
    const logger = useLogger();

    const {TVJoyDialog, confirmDialog, closeDialog} = useDialogRoot();

    const clientViewStore = useClientQuoteViewStore();

    const declineReasons = {
        ALREADY_STAFFED: {
            label: t('projectclient.quote.view.quote-summary.dialog.decline.reason.ALREADY_STAFFED.label'),
            value: 'ALREADY_STAFFED',
        },
        NO_LONGER_AVAILABLE: {
            label: t('projectclient.quote.view.quote-summary.dialog.decline.reason.NO_LONGER_AVAILABLE.label'),
            value: 'NO_LONGER_AVAILABLE',
        },
        DIRECT_HIRE: {
            label: t('projectclient.quote.view.quote-summary.dialog.decline.reason.DIRECT_HIRE.label'),
            value: 'DIRECT_HIRE',
        },
        NOT_RELEVANT: {
            label: t('projectclient.quote.view.quote-summary.dialog.decline.reason.NOT_RELEVANT.label'),
            value: 'NOT_RELEVANT',
        },
        PRICE_OVER_BUDGET: {
            label: t('projectclient.quote.view.quote-summary.dialog.decline.reason.PRICE_OVER_BUDGET.label'),
            value: 'PRICE_OVER_BUDGET',
        },
    };

    type DeclineFormTypes = {
        declineReason: DeclineQuoteRequestReasonEnum | undefined;
        freelancerMessage: string;
    };

    const state = ref<DeclineFormTypes>({
        declineReason: undefined,
        freelancerMessage: '',
    });

    const {r$} = useRegle(state, {
        declineReason: {required, maxLength: maxLength(2000)},
        freelancerMessage: {required, maxLength: maxLength(2000)},
    });

    const isFormValid = computed(() => !r$.$invalid);

    function handleClearValues() {
        r$.declineReason.$value = undefined;
        r$.freelancerMessage.$value = '';
    }

    const loading = ref<boolean>(false);

    async function handleConfirm() {
        r$.$touch();
        try {
            if (isFormValid.value && state.value.declineReason) {
                loading.value = true;
                await clientViewStore.declineQuote(props.quoteId, state.value.declineReason, state.value.freelancerMessage);
                confirmDialog();
            }
        } catch (e) {
            logger.error(e);
        } finally {
            loading.value = false;
            handleClearValues();
            await clientViewStore.getQuote(props.quoteId);
        }
    }
</script>

<style lang="scss" scoped>
    .joy-textarea {
        margin-top: var(--joy-core-spacing-8);
    }
</style>
